export function localizeChatProductURL(href, context) {
  if (!href || href.trim().startsWith('#')) return null;
  let url;
  let page;
  let root;
  try {
    page = new URL(context.pageURL);
    url = new URL(href, page);
    root = new URL(context.root, page);
  } catch {
    return null;
  }
  const prefix = root.pathname.replace(/\/+$/, '');
  if (!prefix || root.origin !== page.origin) return null;
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
  const hosts = new Set([page.host, context.shopDomain].filter(Boolean));
  if (!hosts.has(url.host)) return null;
  const product = url.pathname.match(/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?(\/(?:products\/[^/]+|collections\/[^/]+\/products\/[^/]+))\/?$/i);
  if (!product || /\.(?:js|json)$/i.test(product[1])) return null;
  const original = url.href;
  url.protocol = page.protocol;
  url.host = page.host;
  url.pathname = prefix + product[1] + (url.pathname.endsWith('/') ? '/' : '');
  const preview = page.searchParams.get('preview_theme_id');
  if (preview && !url.searchParams.has('preview_theme_id')) {
    url.searchParams.set('preview_theme_id', preview);
  }
  return url.href === original ? null : url.href;
}

export function installChatLocaleLinks(chat, context) {
  if (!chat) return;
  const win = chat.ownerDocument.defaultView;
  const observed = new WeakSet();
  const waiting = new WeakSet();
  const localized = new WeakMap();
  let active = true;
  const isSpanish = /^\/es(?:\/|$)/.test(new URL(context.root, context.pageURL).pathname);

  const mobileEntry = chat.ownerDocument.querySelector('.dl-coffee-chat-trigger');
  let openedFromMobileEntry = false;
  function connectMobileEntry(element) {
    if (!mobileEntry || !element.matches?.('button[part~="activator"]')) return;
    const label = element.getAttribute('aria-label');
    if (!label) return;
    mobileEntry.setAttribute('aria-label', label);
    mobileEntry.setAttribute('aria-expanded', element.getAttribute('aria-expanded') || 'false');
    mobileEntry.hidden = false;
    chat.ownerDocument.body.setAttribute('data-dl-coffee-chat-ready', '');
  }
  function openFromMobileEntry() {
    openedFromMobileEntry = true;
    chat.setAttribute('mode', 'standalone');
    chat.setAttribute('open', '');
  }
  function restoreMobileFocus() {
    if (openedFromMobileEntry && win.matchMedia('(max-width: 749px)').matches) {
      mobileEntry?.focus({ preventScroll: true });
    }
    openedFromMobileEntry = false;
  }
  mobileEntry?.addEventListener('click', openFromMobileEntry);
  chat.ownerDocument.addEventListener('shopify-chat:close', restoreMobileFocus);

  function rewriteSpanishButton(node) {
    if (!isSpanish) return;
    if (node.nodeType === 3) {
      if (node.parentElement?.closest('button') && node.data.includes('Envíanos un mensaje')) {
        node.data = node.data.replaceAll('Envíanos un mensaje', 'Envíenos un mensaje');
      }
      return;
    }
    if (node.nodeType !== 1) return;
    for (const name of ['aria-label', 'title']) {
      const value = node.getAttribute(name);
      if (value?.includes('Envíanos un mensaje')) {
        node.setAttribute(name, value.replaceAll('Envíanos un mensaje', 'Envíenos un mensaje'));
      }
    }
    for (const child of node.childNodes) {
      if (child.nodeType === 3) rewriteSpanishButton(child);
    }
  }

  function rewrite(link) {
    if (!(link instanceof win.HTMLAnchorElement) || link.hasAttribute('download')) return;
    const href = localizeChatProductURL(link.getAttribute('href'), context);
    if (href) {
      link.setAttribute('href', href);
      localized.set(link, href);
    }
  }

  const observer = new win.MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes') { rewrite(record.target); connectMobileEntry(record.target); }
      if (record.type === 'attributes' || record.type === 'characterData') rewriteSpanishButton(record.target);
      for (const node of record.addedNodes) scan(node);
    }
  });

  function scan(node) {
    if (!active) return;
    rewriteSpanishButton(node);
    if (!node.querySelectorAll) return;
    if ((node === chat || node instanceof win.ShadowRoot) && !observed.has(node)) {
      observed.add(node);
      observer.observe(node, { childList: true, subtree: true, attributes: true, characterData: isSpanish, attributeFilter: ['href', 'aria-label', 'title', 'aria-expanded'] });
    }
    for (const element of [node, ...node.querySelectorAll('*')]) {
      rewrite(element);
      rewriteSpanishButton(element);
      connectMobileEntry(element);
      if (element.shadowRoot) scan(element.shadowRoot);
      if (element.localName?.includes('-') && !waiting.has(element)) {
        waiting.add(element);
        win.customElements.whenDefined(element.localName).then(() => {
          if (active && element.isConnected) scan(element);
        });
      }
    }
  }

  function prepare(event) {
    const path = event.composedPath();
    if (!path.includes(chat)) return;
    const link = path.find((node) => node instanceof win.HTMLAnchorElement);
    if (!link) return;
    rewrite(link);
    if ((event.type === 'click' || event.type === 'auxclick') && localized.get(link) === link.href) {
      event.stopImmediatePropagation();
    }
  }

  const events = ['pointerdown', 'click', 'auxclick', 'contextmenu', 'focusin', 'keydown'];
  for (const type of events) chat.addEventListener(type, prepare, true);
  scan(chat);
  return () => {
    active = false;
    observer.disconnect();
    mobileEntry?.removeEventListener('click', openFromMobileEntry);
    chat.ownerDocument.removeEventListener('shopify-chat:close', restoreMobileFocus);
    for (const type of events) chat.removeEventListener(type, prepare, true);
  };
}

if (typeof document !== 'undefined') {
  const chat = document.querySelector('#chat-drawer shopify-chat');
  if (chat) {
    installChatLocaleLinks(chat, {
      pageURL: window.location.href,
      root: window.Shopify?.routes?.root || chat.dataset.donglinLocaleRoot || '/',
      shopDomain: chat.dataset.donglinShopDomain,
    });
  }
}
